// Match a user-entered registered name against AKC's published Fast CAT rankings.
// Real-world drift between the two:
//   - Casing: user types "DL's Believe It or Not", AKC has "Dl's Believe It Or Not"
//   - Apostrophes: user has curly ’, AKC has straight '
//   - Whitespace: stray double-spaces
//   - Trailing title suffixes: user may include "FDC RN DCAT CHCA CGCU ATT" or none
//   - Leading champion prefix: "CH" / "GCH" / etc on either side
//
// Strategy: normalize both, optionally strip champion prefix + trailing all-caps
// title tokens, then check bidirectional substring containment.

const CHAMPION_PREFIX = /^(CH|GCH|GCHB|GCHS|GCHG|GCHP|MBIS|MBISS)\s+/i;
const TRAILING_TITLES = /(\s+[A-Z]{2,}\d*)+\s*$/;

function baseNormalize(s: string | null | undefined): string {
  if (!s) return "";
  // Strip all punctuation so "Moons" matches "Moon's", "DLs" matches "Dl's",
  // "Don't" matches "Dont", etc. Treats curly/straight apostrophes the same.
  return s
    .toLowerCase()
    .replace(/[^\w\s]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

function stripTitles(s: string | null | undefined): string {
  if (!s) return "";
  // Strip leading champion prefix + trailing title acronyms while preserving
  // the case-sensitive title pattern; then run through baseNormalize to also
  // drop punctuation and lowercase.
  const noPrefix = s.replace(CHAMPION_PREFIX, "");
  const noTrailing = noPrefix.replace(TRAILING_TITLES, "");
  return baseNormalize(noTrailing);
}

// True if two strings are within Levenshtein distance 1 (one sub/ins/del).
// O(n) scan — sufficient for common single-character typos.
function withinOneEdit(a: string, b: string): boolean {
  if (a === b) return true;
  const m = a.length, n = b.length;
  if (Math.abs(m - n) > 1) return false;
  if (m === n) {
    let diffs = 0;
    for (let i = 0; i < m; i++) {
      if (a[i] !== b[i] && ++diffs > 1) return false;
    }
    return true;
  }
  const [shorter, longer] = m < n ? [a, b] : [b, a];
  let i = 0, j = 0, skipped = false;
  while (i < shorter.length && j < longer.length) {
    if (shorter[i] === longer[j]) { i++; j++; }
    else if (!skipped) { j++; skipped = true; }
    else return false;
  }
  return true;
}

// Token-overlap fuzzy: require ≥75% of user's significant tokens (≥3 chars)
// to appear in AKC's name either exactly, as substring, or within one
// character. Breed constraint upstream keeps false-positive risk low.
function fuzzyTokenMatch(userNorm: string, akcNorm: string): boolean {
  const userTokens = userNorm.split(/\s+/).filter((t) => t.length >= 3);
  const akcTokens = akcNorm.split(/\s+/).filter((t) => t.length >= 3);
  if (userTokens.length === 0 || akcTokens.length === 0) return false;
  let matched = 0;
  for (const ut of userTokens) {
    if (akcTokens.some((at) =>
      at === ut || at.includes(ut) || ut.includes(at) || withinOneEdit(ut, at)
    )) matched++;
  }
  return matched / userTokens.length >= 0.75;
}

export interface NameLike { dogName: string }

export function findAkcMatch<T extends NameLike>(
  rankings: T[],
  registeredName: string | null | undefined,
): T | null {
  if (!registeredName) return null;

  // Pass 1: simple normalize, fast substring contain
  const userNorm = baseNormalize(registeredName);
  if (userNorm) {
    const hit = rankings.find((r) => {
      const akcNorm = baseNormalize(r.dogName);
      return akcNorm === userNorm || akcNorm.includes(userNorm) || userNorm.includes(akcNorm);
    });
    if (hit) return hit;
  }

  // Pass 2: strip champion prefix + trailing title tokens from BOTH sides
  const userStripped = stripTitles(registeredName);
  if (!userStripped) return null;
  const titleHit = rankings.find((r) => {
    const akcStripped = stripTitles(r.dogName);
    if (!akcStripped) return false;
    return akcStripped === userStripped
      || akcStripped.includes(userStripped)
      || userStripped.includes(akcStripped);
  });
  if (titleHit) return titleHit;

  // Pass 3: fuzzy token-overlap with one-char typo tolerance. Only runs after
  // exact passes fail, so it can only ADD matches — never change existing ones.
  return rankings.find((r) => fuzzyTokenMatch(userStripped, stripTitles(r.dogName))) ?? null;
}
